<template>
    <section>
      <b-card class="mb-3 p-3">
        <b-row class="align-items-center text-center text-md-left">
          <!-- Company Info -->
          <b-col md="3" class="mb-2 mb-md-0">
            <h5 class="mb-1">{{ companyData?.name }}</h5>
            <p class="mb-0"><strong>Email:</strong> {{ companyData?.email }}</p>
            <p class="mb-0"><strong>Contact:</strong> {{ companyData?.contact_no }}</p>
            <p v-if="companyData?.website" class="mb-0">
              <strong>Website:</strong> <a :href="companyData.website" target="_blank">{{ companyData.website }}</a>
            </p>
          </b-col>

          <!-- Subscription Info -->
          <b-col md="3" class="mb-2 mb-md-0">
            <p class="mb-1"><strong>Package ID:</strong> {{ companyData?.package_id }}</p>
            <p class="mb-1">
              <strong>Max Users:</strong> {{ companyData?.max_user }}
            </p>
            <p class="mb-1">
              <strong>Subscription Expire:</strong> 
              <span :class="getSubscriptionStatusClass(companyData?.subscription_expire_date)">
                {{ formatDateTime(companyData?.subscription_expire_date) }}
              </span>
            </p>
            <p v-if="companyData?.referral_id" class="mb-0">
              <strong>Referral ID:</strong> {{ companyData.referral_id }}
            </p>
          </b-col>

          <!-- Payment Info -->
          <b-col md="3" class="mb-2 mb-md-0">
            <p class="mb-1">
              <strong>Last Payment:</strong> 
              {{ subscriptionExpireDate?.currency?.toUpperCase() }} {{ subscriptionExpireDate?.amount_paid }}
            </p>
            <p class="mb-1">
              <strong>Next Payment Date:</strong> {{ formatDateTime(subscriptionExpireDate?.next_payment_date) }}
            </p>
            <p class="mb-1">
              <strong>Payment Method:</strong> {{ getPaymentMethodName(companyData?.payment_method) }}
            </p>
          </b-col>

          <!-- Status -->
          <b-col md="3" class="text-md-right">
            <b-badge :variant="getStatusVariant(companyData?.status)" class="p-2">
              {{ getStatusText(companyData?.status) }}
            </b-badge>
            <p class="mt-2 mb-0" v-if="companyData?.pause_date">
              <strong>Paused On:</strong> {{ formatDateTime(companyData.pause_date) }}
            </p>
            <p class="mb-0" v-if="companyData?.resume_date">
              <strong>Resumed On:</strong> {{ formatDateTime(companyData.resume_date) }}
            </p>
          </b-col>
        </b-row>
      </b-card>


      <b-card>
        <div>
          <!-- table -->
          <vue-good-table
            styleClass="vgt-table table-custom-style striped"
            :line-numbers="false"
            mode="remote"
            @on-page-change="onPageChange"
            @on-sort-change="onSortChange"
            @on-column-filter="onColumnFilter"
            @on-per-page-change="onPerPageChange"
            :totalRows="totalRecords"
            :isLoading.sync="isLoading"
            :rows="rows"
            :columns="columns"
            :sort-options="{
              enabled: false,
              multipleColumns: true,
              initialSortBy: [{ field: 'created_at', type: 'desc' }],
            }"
            :pagination-options="{
              enabled: true,
              perPage: pageLength,
            }"
          >
            <template slot="table-row" slot-scope="props">
              <!-- payment date -->
              <template v-if="props?.column?.field === 'format_created_at'">
                <div v-if="props.row.created_at">
                  {{ formatDateTime(props.row.created_at) }}
                </div>
              </template>
  
              <template v-if="props?.column?.field === 'format_amount'">
                <div v-if="props.row.amount_paid">
                  <div>
                    {{ props?.row?.currency?.toUpperCase() }}
                    {{ props.row.amount_paid }}
                  </div>
                </div>
              </template>
  
              <!-- next payment date -->
              <template
                v-if="props?.column?.field === 'format_next_payment_date'"
              >
                <div v-if="props.row.next_payment_date">
                  {{ formatDateTime(props.row.next_payment_date) }}
                </div>
              </template>
  
              <template v-if="props?.column?.field === 'format_status'">
                <b-badge variant="light-success">
                  {{ props.row.payment_status }}
              </b-badge>
              </template>
            </template>
  
            <!-- pagination -->
            <template slot="pagination-bottom" slot-scope="props">
              <div class="d-flex justify-content-between flex-wrap">
                <div class="d-flex align-items-center mb-0 mt-1">
                  <span class="text-nowrap"> Showing 1 to </span>
                  <b-form-select
                    v-model="pageLength"
                    :options="['10', '25', '50', '100', '500']"
                    class="mx-1"
                    @input="
                      (value) => props.perPageChanged({ currentPerPage: value })
                    "
                  />
                  <span class="text-nowrap"> of {{ props.total }} entries </span>
                </div>
                <div>
                  <b-pagination
                    :value="1"
                    :total-rows="props.total"
                    :per-page="pageLength"
                    first-number
                    last-number
                    align="right"
                    prev-class="prev-item"
                    next-class="next-item"
                    class="mt-1 mb-0"
                    @input="(value) => props.pageChanged({ currentPage: value })"
                  >
                    <template #prev-text>
                      <feather-icon icon="ChevronLeftIcon" size="18" />
                    </template>
                    <template #next-text>
                      <feather-icon icon="ChevronRightIcon" size="18" />
                    </template>
                  </b-pagination>
                </div>
              </div>
            </template>
          </vue-good-table>
        </div>
      </b-card>
    </section>
  </template>
  
  <script>
  import {
    BCard,
    BAvatar,
    BBadge,
    BPagination,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BDropdown,
    BDropdownItem,
    BButton,
    BForm,
    BModal,
    BSpinner,
    BRow,
    BCol,
    VBTooltip,
    BProgress,
    BProgressBar,
    BCardBody,
    BCardText,
  } from "bootstrap-vue";
  import { VueGoodTable } from "vue-good-table";
  import Ripple from "vue-ripple-directive";
  import { ValidationProvider, ValidationObserver } from "vee-validate";
  import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
  import UserAssignPolicyForm from "@/views/admin/payroll-management/UserAssignPolicyForm.vue";
  
  export default {
    name: "CompanySubscriptionHistory",
    components: {
      UserAssignPolicyForm,
      BForm,
      BButton,
      BCard,
      VueGoodTable,
      BAvatar,
      BBadge,
      BPagination,
      BFormGroup,
      BFormInput,
      BFormSelect,
      BDropdown,
      BDropdownItem,
      ValidationProvider,
      ValidationObserver,
      BModal,
      BSpinner,
      BRow,
      BCol,
      VBTooltip,
      BProgress,
      BProgressBar,
      BCardBody,
      BCardText,
  
    },
    directives: {
      "b-tooltip": VBTooltip,
      Ripple,
    },
    data() {
      return {  
        companyData: null,
        companyId: "",
        paymentMethod: null,
        subscriptionExpireDate: "",
        pageLength: 10,
        columns: [
          {
            label: "Payment Date",
            field: "format_created_at",
            sortable: false,
          },
          {
            label: "Amount",
            field: "format_amount",
            sortable: false,
          },
          {
            label: "Status",
            field: "format_status",
            sortable: false,
          },
          {
            label: "Next Payment Date",
            field: "format_next_payment_date",
            sortable: false,
          },
        ],
        rows: [],
        searchTerm: "",
        delayTimer: null,
        isLoading: false,
        totalRecords: 0,
        serverParams: {
          columnFilters: {},
          sort: [
            { field: "id", type: "desc" },
            { field: "created_at", type: "desc" },
          ],
          page: 1,
          perPage: 10,
        },
      };
    },
  
    async created() {
      this.companyId = this.$route.params.id;
      this.paymentMethod = this.$route.params.payment_method;
      try {
        this.loadItems();
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error",
            icon: "BellIcon",
            variant: "danger",
            text: error?.response?.data?.message,
          },
        });
      }
    },
  
    methods: {
      capitalizeFirstLetter(string) {
        if (!string) return "";
        return string.charAt(0).toUpperCase() + string.slice(1);
      },
      formatDateTime(value) {
        if (value) {
          return this.$moment(value).format("MMM Do YYYY");
        }
      },
  
      formatSubscriptionExpiredDateTime(value) {
        if (value) {
          return this.$moment(value).subtract(1, 'days').format("MMM Do YYYY");
        }
      },
  
      async getPaymentHistory(params) {
        return await this.$api.get("api/super-admin/subscription-history", {
          params: {
            company_id: params.company_id,
            show: params.show,
            page: params.page,
            sort: params.sort,
            q: params.q,
            payment_method: params.payment_method,
          },
        });
      },
  
      updateParams(newProps) {
        this.serverParams = Object.assign({}, this.serverParams, newProps);
      },
  
      onPageChange(params) {
        this.updateParams({ page: params.currentPage });
        this.loadItems();
      },
  
      onColumnFilter(params) {
        this.updateParams(params);
        this.loadItems();
      },
  
      onPerPageChange(params) {
        this.updateParams({ perPage: params.currentPerPage });
        this.loadItems();
      },
  
      onSortChange(params) {
        this.updateParams({
          sort: params,
        });
        this.loadItems();
      },
  
      async loadItems() {
        try {
          const paymentHistories = await this.getPaymentHistory({
            company_id: this.companyId,
            show: this.serverParams.perPage,
            page: this.serverParams.page,
            sort: this.serverParams.sort,
            q: this.searchTerm,
            payment_method: this.paymentMethod,
          });
  
          const data = paymentHistories?.data?.data;
          const meta = paymentHistories?.data?.meta;
          this.subscriptionExpireDate = meta?.subscriptionExpired?.next_payment_date
          
          this.companyData = paymentHistories.data.meta.companyData;
          this.totalRecords = meta?.pagination?.total;
          this.rows = data;

        } catch (error) {
          console.log(error);
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Warning",
              icon: "BellIcon",
              variant: "warning",
              text: error?.response?.data?.message,
            },
          });
        }
      },
  
      getStatusVariant(status) {
          switch(status) {
            case 1:
              return 'light-success'; // Green for completion
            case 2:
                return 'light-success'; // Green for payment in progress
            case 3:
                return 'light-danger'; // Red for paused state
            case 4:
                return 'light-primary'; // Blue for resumed state
            case 5:
                return 'light-danger'; // Red for cancellation
            case 6:
                return 'light-warning';;
              default:
                  return 'light-secondary'; // default color
          }
      },
      getStatusText(status) {
          switch(status) {
              case 1:
                  return 'Complete';
              case 2:
                  return 'On Payment';
              case 4:
                  return 'Resume';
              case 'cancel':
                  return 'Inactive';
              case 3:
                  return 'Paused';
              case 6:
                  return 'Trial';
              case 5:
                  return 'Cancel';
              default:
                  return 'Unknown';
          }
      },
      getSubscriptionStatusClass(date) {
        if (!date) return "text-secondary";
        const today = this.$moment();
        const expire = this.$moment(date);
        if (expire.isBefore(today)) return "text-danger font-weight-bold";
        if (expire.diff(today, "days") <= 7) return "text-warning font-weight-bold";
        return "text-success font-weight-bold";
      },
      getPaymentMethodName(method) {
        const methods = {
          1: "Stripe",
          2: "PayPal",
          3: "Bank Transfer",
          4: "Manual",
        };
        return methods[method] || "Unknown";
      },
    },
  };
  </script>
  
  <style lang="scss">
  @import "@core/scss/vue/libs/vue-good-table.scss";
  
  .required-label label::after {
    content: " *";
    color: red;
  }
  </style>